import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo } from "react";
import { useMediaQuery } from "react-responsive";
import classNames from "classnames";
import dompurify from "isomorphic-dompurify";
import { getConfig } from "@jti/fe-common";
import { useMessageBus } from "@jti/fe-message-bus";
import { Button } from "@jti/ui/components/Button";
import { LanguageSelector } from "@jti/ui/components/LanguageSelector";
import { usePortal } from "../../hooks/usePortal";
import { useUser } from "../../hooks/useUser";
import { WelcomePopupImage } from "./WelcomePopupImage";
import "./WelcomePopup.global.scss";
import styles from "./WelcomePopup.module.scss";
const { sanitize } = dompurify;
export const WelcomePopup = ({ title, paragraph, loginButtonLabel, bottomText, legalNotice, imageSrc, tabletImageSrc, mobileImageSrc, logo, languageSelector = [], alt, socialMediaLinks, socialMediaText, }) => {
    const config = getConfig();
    const { isLoggedIn, hasAV, isUserVerificationPending } = useUser(true);
    const { renderPortal } = usePortal();
    const messageBus = useMessageBus();
    const isSmallScreen = useMediaQuery({ query: "(max-width: 959px)" });
    const showModal = useMemo(() => {
        if (isLoggedIn === null) {
            return false;
        }
        return !isLoggedIn || (isLoggedIn && !hasAV && !isUserVerificationPending);
    }, [isLoggedIn, hasAV, isUserVerificationPending]);
    const goToLogin = () => {
        window.location.pathname = config.AUTH_PAGE_URL;
    };
    const handleSocialClick = (event, socialNetwork) => {
        event.preventDefault();
        messageBus.publish("tracking.event", {
            payload: {
                event_name: "follow",
                additionalData: {
                    social_network: socialNetwork,
                },
            },
        });
        window.open(event.currentTarget.href, event.currentTarget.target);
    };
    useEffect(() => {
        window.document.body.classList.toggle("body--popup-open", showModal);
    }, [showModal]);
    return showModal
        ? renderPortal(_jsxs("div", { className: classNames("aem-welcome-popup", "light-theme", styles.welcomePopup), children: [_jsx("div", { className: styles.backdrop }), _jsxs("div", { className: styles.container, children: [_jsxs("div", { className: styles.content, children: [languageSelector.length > 1 && (_jsx(LanguageSelector, { languages: languageSelector, popupContext: "NAVIGATION", variant: "SHORT_LANGUAGE_NAME" })), _jsxs("div", { className: styles.wrapper, children: [_jsx("img", { src: logo, alt: "logo", className: styles.logo }), _jsx("h2", { className: styles.title, children: title }), isSmallScreen && (_jsx(WelcomePopupImage, { largeSrc: imageSrc, mediumSrc: tabletImageSrc, smallSrc: mobileImageSrc, alt: alt })), paragraph && (_jsx("div", { className: styles.text, dangerouslySetInnerHTML: {
                                                __html: sanitize(paragraph),
                                            } })), _jsx(Button, { onClick: goToLogin, className: styles.loginButton, color: "primary", children: loginButtonLabel }), bottomText && (_jsx("p", { className: styles.bottomText, dangerouslySetInnerHTML: {
                                                __html: sanitize(bottomText),
                                            } })), socialMediaLinks && (_jsxs("div", { className: styles.socials, children: [_jsx("div", { className: styles.socialsWrapper, children: socialMediaLinks.map((socialItem) => (_jsx("a", { className: styles.socialLink, href: socialItem.link, target: "_blank", rel: "noreferrer", "data-testid": socialItem.trackingName, onClick: (event) => handleSocialClick(event, socialItem.trackingName), children: _jsx("img", { className: styles.socialImg, src: socialItem.image, alt: socialItem.alt }) }, socialItem.trackingName))) }), socialMediaText && _jsx("p", { className: styles.socialText, children: socialMediaText })] })), legalNotice && (_jsx("div", { className: styles.legalNotice, children: _jsx("span", { dangerouslySetInnerHTML: {
                                                    __html: sanitize(legalNotice),
                                                } }) }))] })] }), !isSmallScreen && (_jsx(WelcomePopupImage, { largeSrc: imageSrc, mediumSrc: tabletImageSrc, smallSrc: mobileImageSrc, alt: alt }))] })] }))
        : null;
};
//# sourceMappingURL=WelcomePopup.js.map